<template>
  <div class="exam-list">
    <HDFHeader03 />
    
    <div class="page-wrapper">
      <el-container>
        <el-header class="page-header">
          <h1>考试报名列表</h1>
          <el-button type="primary" @click="$router.push('/exam-history')" plain>
            查看报名历史
          </el-button>
        </el-header>
        
        <el-main>
          <el-row :gutter="20">
            <el-col :span="24">
              <el-alert
                title="请选择您要报名的考试项目"
                type="info"
                :closable="false"
                show-icon
                class="info-alert"
              />
            </el-col>
          </el-row>
          
          <el-row :gutter="20" class="exam-grid">
            <el-col 
              v-for="exam in examList" 
              :key="exam.id"
              :xs="24" 
              :sm="12" 
              :md="8" 
              :lg="6"
            >
              <el-card 
                class="exam-card"
                :class="{ 'disabled': exam.disabled }"
                shadow="hover"
              >
                <div class="exam-card-content">
                  <div class="exam-image">
                    <img :src="exam.firstImg" :alt="exam.title" @error="handleImageError">
                  </div>
                  
                  <div class="exam-info">
                    <h3 class="exam-title">{{ exam.title }}</h3>
                    <p class="exam-description">{{ exam.description }}</p>
                    
                    <div class="exam-details">
                      <div class="detail-item">
                        <el-icon><Calendar /></el-icon>
                        <span>报名开始时间: {{ exam.registrationStartDate }}</span>
                      </div>
                      
                      <div class="detail-item">
                        <el-icon><Clock /></el-icon>
                        <span>报名截止时间: {{ exam.registrationEndDate }}</span>
                      </div>
                      
                      <div class="detail-item">
                        <el-icon><Location /></el-icon>
                        <span>{{ exam.locationDescription }}</span>
                      </div>
                    </div>
                    
                    <div class="exam-status" :class="getStatusClass(exam.status)">
                      <el-tag :type="getStatusTagType(exam.status)">
                        {{ statusText(exam.status) }}
                      </el-tag>
                    </div>
                  </div>
                  
                  <div class="exam-actions">
                    <el-button 
                      v-if="!exam.submit"
                      type="primary" 
                      :disabled="exam.disabled"
                      @click="goToRegistration(exam)"
                      class="register-button"
                    >
                    {{ exam.status == 'enable' ? '立即报名' : '未报名' }}
                    </el-button>
                    <el-button 
                      v-if="exam.submit"
                      type="primary"
                      @click="goToShowDetail(exam)"
                      class="register-button"
                    >
                    {{ exam.submit ? '已报名（查看详情）' : statusText(exam.status) }}
                    </el-button>
                  </div>
                </div>
              </el-card>
            </el-col>
          </el-row>
          
          <el-row v-if="examList.length === 0" justify="center" class="empty-state">
            <el-empty description="暂无考试项目信息" />
          </el-row>
        </el-main>
      </el-container>
    </div>

    <HDFFooter03 />
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { Calendar, Clock, Location } from '@element-plus/icons-vue'
import HDFHeader03 from '@/components/HDFHeader03.vue'
import HDFFooter03 from '@/components/HDFFooter03.vue'

import { apiGetExamSessionList } from '@/api/exam'

const router = useRouter()

// 考试列表数据
const examList = ref([])

// 获取状态标签类型
const getStatusTagType = (status) => {
  const statusMap = {
    '未开放': 'info',
    '报名中': 'success',
    '已截止': 'warning',
    '考试已结束': 'danger'
  }
  return statusMap[status] || 'info'
}

// 获取状态CSS类
const getStatusClass = (status) => {
  return `status-${status.replace(/[\u4e00-\u9fa5]+/g, '')}`
}

// 跳转到报名页面
const goToRegistration = (exam) => {
  // 跳转到报名页面，并且传参id和title
  router.push({
    path: '/exam-registration',
    query: {
      id: exam.id,
      title: exam.title
    }
  })
}

const goToShowDetail = (exam) => {
  router.push({
    path: '/exam-status',
    query: {
      id: exam.id
    }
  })
}

// 处理图片加载错误
const handleImageError = (e) => {
  e.target.src = 'https://via.placeholder.com/300x160?text=暂无图片'
}

const statusText = (status) => {
  if (status === 'before') {
    return '未开始'
  } else if (status === 'enable') {
    return '报名中'
  } else if (status === 'after') {
    return '截止报名'
  } else if (status === 'end') {
    return '考试已结束'
  } else if (status === 'cancel') {
    return '取消考试'
  } else if (status === 'result') {
    return '成绩已公布'
  }
}

onMounted(() => {
  apiGetExamSessionList().then((response) => {
    const res = response.data
    if (res.code === 200) {
      examList.value = []
      examList.value = res.data
    }
  })
})

</script>

<style scoped>
.exam-list {
  min-height: 100vh;
  background-color: #f5f7fa;
}

.page-wrapper {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 0;
}

.page-header h1 {
  margin: 0;
  color: #303133;
}

.info-alert {
  margin-bottom: 20px;
}

.exam-grid {
  margin-top: 20px;
}

.exam-card {
  margin-bottom: 20px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.exam-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}

.exam-card.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.exam-card-content {
  padding: 0;
}

.exam-image {
  height: 160px;
  overflow: hidden;
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
}

.exam-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.exam-info {
  padding: 16px;
}

.exam-title {
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #303133;
  line-height: 1.4;
}

.exam-description {
  margin: 0 0 16px 0;
  font-size: 13px;
  color: #606266;
  line-height: 1.5;
  min-height: 40px;
}

.exam-details {
  margin-bottom: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 12px;
  color: #909399;
}

.detail-item .el-icon {
  margin-right: 6px;
  font-size: 14px;
}

.exam-status {
  margin-bottom: 16px;
  text-align: center;
}

.exam-actions {
  padding: 0 16px 16px;
}

.register-button {
  width: 100%;
}

.empty-state {
  margin-top: 50px;
}

@media (max-width: 768px) {
  .page-header {
    flex-direction: column;
    gap: 15px;
  }
  
  .exam-grid {
    margin-top: 10px;
  }
  
  .page-wrapper {
    padding: 0 10px;
  }
}
</style>